import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, DataTable, MoneyDisplay, type Column } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { useLocalizedText } from '@shared/lib/use-localized';
import type { ProductView } from '@entities/product';
import { useCategoriesQuery, useCategoryName } from '@entities/taxonomy';
import { ProductStatusChip } from './product-status-chip';
import { ProductThumb } from './product-thumb';

interface ProductsTableProps {
  data: ProductView[];
}

export function ProductsTable({ data }: ProductsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Product list shows the PRODUCT category — fetch with that kind explicitly
  // so we don't pollute the cache with PRODUCER kinds we don't need here.
  const { data: categories } = useCategoriesQuery(['PRODUCT']);
  const localize = useCategoryName();
  const localizeText = useLocalizedText();
  const categoryById = new Map(categories?.map((c) => [c.id, localize(c)]) ?? []);

  const columns: Column<ProductView>[] = [
    {
      key: 'photo',
      header: '',
      width: '60px',
      cell: (p) => <ProductThumb link={p.photos[0]} alt={localizeText(p.name)} />,
    },
    {
      key: 'name',
      header: t('products.columns.name'),
      cell: (p) => (
        <div className="flex flex-col">
          <span className="font-medium">{localizeText(p.name)}</span>
          <span className="text-xs text-muted-foreground">
            {categoryById.get(p.categoryId) ?? p.categoryId}
          </span>
        </div>
      ),
    },
    {
      key: 'price',
      header: t('products.columns.price'),
      cell: (p) => (
        <MoneyDisplay money={p.exportPrice} />
      ),
    },
    {
      key: 'moq',
      header: t('products.columns.moq'),
      cell: (p) => p.moq,
    },
    {
      key: 'flags',
      header: t('products.columns.flags'),
      cell: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.stock ? <Badge tone="info">Stock</Badge> : null}
          {p.eco ? <Badge tone="success">Eco</Badge> : null}
          {p.stock && p.stockDiscountPct ? (
            <Badge tone="warning">-{p.stockDiscountPct}%</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      cell: (p) => <ProductStatusChip status={p.status} title={p.rejectionReason ?? undefined} />,
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      rowKey={(p) => p.id}
      onRowClick={(p) => navigate(ROUTES.productEdit(p.id))}
    />
  );
}
